/**
 * Path collapsing replaces a segment of a traversal path with a single virtual
 * reference, hiding the components in between.
 *
 * ## Matching and translation
 *
 * Rules match traversal paths at type level. The resulting index range
 * addresses that type-level path, so `getCollapsedRange` translates it per
 * instance path before it is applied.
 *
 *   index         0            1            2            3
 *
 *   type path     BC   ─ref─>  App
 *                 └─── rule ───┘
 *
 *   depth 0       Root ─ref─>  App-0
 *                 └─── rule ───┘  ✓
 *
 *   depth 1       Root ─par─>  Child ─ref─> App-1
 *                 └─── rule ───┘  ✗
 *                              └── shift ───┘  ✓
 *
 *   depth 2       Root ─par─>  Child ─par─> Grand ─ref─> App-2
 *                 └─── rule ───┘  ✗
 *                                           └── shift ───┘  ✓
 *
 *   ─ref─>  the reference the rule names
 *   ─par─>  a parent-child hop the start-set expansion prepended
 *   rule    the matched range, applied as written
 *   shift   the same range, moved past the prepended hops
 *
 * ## Removal, in two phases
 *
 * A component can sit on several paths, so removal is decided in two steps:
 *
 * 1. The references a collapsed segment replaces are removed outright.
 * 2. The components along the segment, and the references linking to them, are
 *    marked as candidates.
 *
 * Once every path has been processed a candidate survives if an uncollapsed
 * path registered it as kept; the rest are removed.
 *
 * ## Recursive rules
 *
 * A start-set expansion prepends one parent-child hop per hierarchy level to
 * each instance path - hops the type-level path does not carry. Setting
 * `collapseRecursively` on a rule anchored at the start of the path absorbs
 * that variable prefix, allocating the connections of the descendants to an
 * ancestor. It stands in for what would otherwise need one rule per depth,
 * which cannot be authored: depth is data, not schema.
 *
 *   depth 2       Root ─par─>  Child ─par─> Grand ─ref─> App-2
 *                 └──────── collapse recursively ───────┘
 *
 *                 Root ═══════════════════════════════> App-2
 *
 * `keepExpandedLevels` says how many levels stay as they are, moving that anchor
 * down the hierarchy. At `1` the root's children keep their own connections, and
 * an instance path that never reaches that level is left untouched:
 *
 *   depth 0       Root ─ref─>  App-0                     untouched
 *
 *   depth 1       Root ─par─>  Child ─ref─> App-1
 *                              └── collapse ─┘
 *                              Child ═══════> App-1
 *
 *   depth 2       Root ─par─>  Child ─par─> Grand ─ref─> App-2
 *                              └─────── collapse ───────┘
 *                              Child ═════════════════> App-2
 *
 * Several recursive rules can share the same start node, one per traversal
 * path. A path that no rule collapses is dropped because it branches off a
 * collapsed segment, exactly as it would for a rule without recursion.
 */
import {
  APIComponentAttributes,
  ArdoqId,
  ComponentPath,
  FEParentChildReferenceAttributes,
  isTraversalLoadedState,
  LoadedState,
  Path,
  PathCollapsingRule,
  PathMatch,
  ReferenceDirection,
  StartSetExpansion,
  TraversalLoadedState,
  VIRTUAL_PARENT_REF_TYPE_NAME,
} from '@ardoq/api-types';
import { VirtualReference } from '@ardoq/data-model';
import { uniq, uniqBy } from 'lodash';
import {
  getMatchesForPath,
  invertTriplePath,
  isSameTriple,
  assignRegionsToRules,
  getMatchingPaths,
  keepLongestMatchPerEndPosition,
  validateMatchesAgainstOverlaps,
} from './pathAndGraphUtils';
import { toByIdDictionary, zip } from '@ardoq/common-helpers';
import { logError } from '@ardoq/sentry';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';

export type ProcessCollapsingRulesArgs = {
  loadedStates: LoadedState[];
};

export const applyPathCollapsingRules = (
  loadedStates: LoadedState[]
): {
  loadedStates: LoadedState[];
  scopeReferenceIds: string[];
  scopeComponentIds: string[];
  parentChildReferences: FEParentChildReferenceAttributes[];
  newReferences: VirtualReference[];
} => {
  const collapsedStates = loadedStates.map(loadedState =>
    toCollapsedLoadedState(loadedState)
  );

  const collapsedLoadedStates = collapsedStates.map(
    ({ loadedState }) => loadedState
  );
  const newReferences = uniqBy(
    collapsedStates.flatMap(({ newReferences }) => newReferences),
    'referenceId'
  );
  const collapsedScopeComponentIds = uniq(
    collapsedStates.flatMap(({ scopeComponentIds }) => scopeComponentIds)
  );
  const collapsedScopeReferenceIds = uniq(
    collapsedStates.flatMap(({ scopeReferenceIds }) => scopeReferenceIds)
  );
  const collapsedParentChildReferences = uniqBy(
    collapsedStates.flatMap(
      ({ parentChildReferences }) => parentChildReferences
    ),
    '_id'
  );

  return {
    loadedStates: collapsedLoadedStates,
    newReferences,
    scopeComponentIds: collapsedScopeComponentIds,
    scopeReferenceIds: collapsedScopeReferenceIds,
    parentChildReferences: collapsedParentChildReferences,
  };
};

const toCollapsedLoadedState = (
  loadedState: LoadedState
): {
  loadedState: LoadedState;
  scopeReferenceIds: string[];
  scopeComponentIds: string[];
  parentChildReferences: FEParentChildReferenceAttributes[];
  newReferences: VirtualReference[];
} => {
  const scopeComponentIds =
    loadedStateOperations.getScopeComponentIds(loadedState);
  const scopeReferenceIds = loadedStateOperations.getReferenceIds(loadedState);
  const parentChildReferences =
    loadedStateOperations.getFEParentChildReferences(loadedState);

  if (!isTraversalLoadedState(loadedState)) {
    return {
      loadedState,
      newReferences: [],
      scopeReferenceIds,
      scopeComponentIds,
      parentChildReferences,
    };
  }

  try {
    const componentsById = toByIdDictionary(loadedState.scopeData.components);
    const { pathResults, paths, pathCollapsingRules, startSetExpansion } =
      getPathsCollapsingRulesAndPathResults(loadedState);
    const accumulatedResult: AccumulatedResult = {
      newReferences: [],
      // Only references replaced by a collapsed path are directly marked for
      // removal. Components on a collapsed path and references linking to them
      // are in a first step only candidates to be removed, as they can also be
      // part of another path which is not collapsed. After all paths have been
      // processed, the candidates which are not registered to be kept are
      // marked for removal.
      removedReferenceIds: new Set(),
      removedReferenceIdCandidates: new Set(),
      removedComponentIdCandidates: new Set(),
      componentIdsToKeep: new Set(),
      referencesToKeep: new Set(),
      collapsedPathIndexes: new Set(),
    };

    // Collapse the instance paths which belong to a path with a matching
    // collapsing rule.
    const collapsedInstancePaths = collapseAllInstancePaths({
      paths,
      pathResults,
      pathCollapsingRules,
      accumulatedResult,
      componentsById,
      startSetExpansion,
    });

    // Slice of all paths branching off of a collapsed subpath.
    const allCollapsedInstancePaths = removeAllBranchesOfCollapsedPaths({
      collapsedInstancePaths: collapsedInstancePaths,
      accumulatedResult,
    });

    // Components can be linked by several references, so components of a
    // collapsed path are in a first step only candidates to be removed.
    const removedComponentIds =
      accumulatedResult.removedComponentIdCandidates.difference(
        accumulatedResult.componentIdsToKeep
      );
    // References which are part of a collapsed path are directly marked for
    // removal, but also references linking to components of a collapsed path
    // which are not part of another non-collapsed path.
    const removedReferenceIds = accumulatedResult.removedReferenceIds.union(
      accumulatedResult.removedReferenceIdCandidates.difference(
        accumulatedResult.referencesToKeep
      )
    );
    const uniqNewReferences = uniqBy(
      accumulatedResult.newReferences,
      'referenceId'
    );
    const updateLoadedState = {
      ...loadedState,
      componentIdsAndReferences: {
        ...loadedState.componentIdsAndReferences,
        pathResults: allCollapsedInstancePaths,
      },
    };
    const startSetResultTraversalsAndSearches = new Set(
      loadedState.componentIdsAndReferences?.startSetResult ?? []
    );
    const collapsedScopeComponentIds = scopeComponentIds.filter(
      id =>
        !removedComponentIds.has(id) ||
        startSetResultTraversalsAndSearches.has(id)
    );
    const collapsedScopeReferenceIds = [
      ...scopeReferenceIds.filter(id => !removedReferenceIds.has(id)),
      ...uniqNewReferences.map(({ referenceId }) => referenceId),
    ];
    const collapsedParentChildReferences = parentChildReferences.filter(
      ({ _id }) => !removedReferenceIds.has(_id)
    );

    return {
      loadedState: updateLoadedState,
      newReferences: uniqNewReferences,
      scopeComponentIds: collapsedScopeComponentIds,
      scopeReferenceIds: collapsedScopeReferenceIds,
      parentChildReferences: collapsedParentChildReferences,
    };
  } catch (error) {
    logError(Error('Error processing path collapsing rules'));
    return {
      loadedState,
      newReferences: [],
      scopeComponentIds,
      scopeReferenceIds,
      parentChildReferences,
    };
  }
};

type CollapseAllInstancePathsArgs = {
  paths: Path[];
  pathResults: ComponentPath[][];
  pathCollapsingRules: PathCollapsingRule[];
  accumulatedResult: AccumulatedResult;
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  startSetExpansion: StartSetExpansion | undefined;
};

const collapseAllInstancePaths = ({
  paths,
  pathResults,
  pathCollapsingRules,
  accumulatedResult,
  componentsById,
  startSetExpansion,
}: CollapseAllInstancePathsArgs) => {
  // Which rule collapses which region is decided across all the paths at once,
  // then applied per path below.
  const assignedRegions = getAssignedRegions(paths, pathCollapsingRules);
  const pathsWithResults = zip(paths, pathResults);
  return pathsWithResults.map(([path, pathResults], index) => {
    const matches = getAllMatchesForPath(
      path,
      pathCollapsingRules,
      index,
      assignedRegions
    );
    if (matches.length === 0) {
      return pathResults;
    }
    accumulatedResult.collapsedPathIndexes.add(index);
    // Matches are sorted by end index in descending order, so that collapsing a
    // path towards the end of the path does not interfere with the indexes of
    // earlier matches. They are also validated against overlaps.
    const collapsedPathResults = matches.reduce((pathResults, match) => {
      return pathResults.map(componentPath => {
        const range = getCollapsedRange(
          match,
          componentPath,
          path,
          startSetExpansion
        );
        if (!range) {
          return componentPath;
        }
        const { startIndexInPath, endIndexInPath } = range;
        return collapsePath({
          componentPath,
          rule: match.rule,
          startIndexInPath,
          endIndexInPath,
          start: match.match.start,
          accumulatedResult,
          componentsById,
        });
      });
    }, pathResults);
    // We can only register the components and references to keep after all
    // collapsing has been done for a path, e.g. all matches for a given are
    // processed, because we don't want to keep components which are part of a
    // path which is later collapsed. Only the components and references of the
    // final collapsed path should be kept.
    collapsedPathResults.forEach(componentPath =>
      registerComponentsAndReferencesToKeep(accumulatedResult, componentPath)
    );
    return collapsedPathResults;
  });
};

// The reference type a parent-child hop carries in a traversal's path.

type CollapsedRange = {
  startIndexInPath: number;
  endIndexInPath: number;
};

// Without an expansion the instance path follows the traversal's path hop for
// hop, so the match addresses it directly.
const getRangeWithoutExpansion = ({
  startIndex,
  length,
}: PathMatch): CollapsedRange => ({
  startIndexInPath: startIndex,
  endIndexInPath: startIndex + length,
});

// The rule collapses hops of the traversal's path, which sit behind the hops the
// expansion prepended: the match applies one hierarchy level down, at the
// descendant this instance path reached.
const getRangeForRule = (
  { startIndex, length }: PathMatch,
  hierarchyPrefixLength: number
): CollapsedRange => ({
  startIndexInPath: startIndex + hierarchyPrefixLength,
  endIndexInPath: startIndex + length + hierarchyPrefixLength,
});

// A recursive rule collapses the hierarchy along with its own hops, so the range
// starts at the deepest level the rule keeps - the root when it keeps none. An
// instance path that does not reach that level has nothing below it to collapse
// and is left as it is.
const getRangeForRecursiveRule = (
  { startIndex, length }: PathMatch,
  hierarchyPrefixLength: number,
  keepExpandedLevels: number
): CollapsedRange | null =>
  hierarchyPrefixLength < keepExpandedLevels
    ? null
    : {
        startIndexInPath: keepExpandedLevels,
        endIndexInPath: startIndex + length + hierarchyPrefixLength,
      };

// A start-set expansion prepends one parent-child hop per hierarchy level to the
// instance path. The traversal's path can declare parent-child hops of its own,
// which the instance path carries in the same shape, so the expansion's prefix
// is what the instance path has beyond them.
const getHierarchyPrefixLength = (componentPath: ComponentPath, path: Path) => {
  const hopsInInstancePath = countLeading(
    componentPath.slice(1),
    ({ isParentChildReference }) => isParentChildReference
  );
  const hopsInPath = countLeading(
    path,
    ({ referenceType }) => referenceType === VIRTUAL_PARENT_REF_TYPE_NAME
  );
  return Math.max(0, hopsInInstancePath - hopsInPath);
};

const countLeading = <T>(items: T[], predicate: (item: T) => boolean) => {
  const firstMismatch = items.findIndex(item => !predicate(item));
  return firstMismatch === -1 ? items.length : firstMismatch;
};

/**
 * Where a match falls in a single instance path. The match indexes address the
 * traversal's path, so only an expanded start set makes them differ - and a
 * recursive rule differs again, absorbing the hierarchy instead of skipping it.
 */
const getCollapsedRange = (
  { rule, match }: { rule: PathCollapsingRule; match: PathMatch },
  componentPath: ComponentPath,
  path: Path,
  startSetExpansion: StartSetExpansion | undefined
): CollapsedRange | null => {
  if (!startSetExpansion) {
    return getRangeWithoutExpansion(match);
  }
  const hierarchyPrefixLength = getHierarchyPrefixLength(componentPath, path);
  // Only a rule anchored on the start of the path stands for the hierarchy.
  return rule.collapseRecursively && match.startIndex === 0
    ? getRangeForRecursiveRule(
        match,
        hierarchyPrefixLength,
        rule.collapseRecursively.keepExpandedLevels
      )
    : getRangeForRule(match, hierarchyPrefixLength);
};

type CollapsePathArgs = {
  componentPath: ComponentPath;
  rule: PathCollapsingRule;
  startIndexInPath: number;
  endIndexInPath: number;
  start: 'start' | 'end';
  accumulatedResult: AccumulatedResult;
  componentsById: Record<ArdoqId, APIComponentAttributes>;
};

const collapsePath = ({
  componentPath,
  rule,
  startIndexInPath,
  endIndexInPath,
  start,
  accumulatedResult,
  componentsById,
}: CollapsePathArgs) => {
  if (componentPath.length < endIndexInPath + 1) {
    const cutOffSubPath = componentPath.slice(startIndexInPath + 1);
    const collapsedPath = componentPath.slice(0, startIndexInPath + 1);
    markComponentAndReferencesToRemove(accumulatedResult, cutOffSubPath);
    // We can only register the component and reference to keep after all
    // collapsing rules of a path have been applied, because we don't want to
    // keep components which are part of a path which is later collapsed.
    // Only the components and references of the final collapsed path should be
    // kept.
    return collapsedPath;
  }

  const startComponent =
    start === 'start'
      ? componentPath[startIndexInPath]
      : componentPath[endIndexInPath];
  const endComponent =
    start === 'start'
      ? componentPath[endIndexInPath]
      : componentPath[startIndexInPath];
  const { referenceTypeId, displayText, referenceStyle } = rule;
  const reference: VirtualReference = {
    sourceId: startComponent.componentId,
    targetId: endComponent.componentId,
    referenceId: getReferenceId(
      startComponent.componentId,
      endComponent.componentId
    ),
    referenceStyle,
    name: displayText,
    referenceTypeId,
    rootWorkspace: getRootWorkspace(componentsById, startComponent.componentId),
    targetWorkspace: getRootWorkspace(componentsById, endComponent.componentId),
  };
  accumulatedResult.newReferences.push(reference);

  const lastComponentId = componentPath[endIndexInPath].componentId;
  const subPathToBeCollapsed = componentPath.slice(
    startIndexInPath + 1,
    endIndexInPath + 1
  );

  subPathToBeCollapsed.forEach(({ referenceId, componentId }) => {
    accumulatedResult.removedReferenceIds.add(referenceId);
    if (componentId !== lastComponentId) {
      accumulatedResult.removedComponentIdCandidates.add(componentId);
    }
  });

  const collapsedPath = [
    ...componentPath.slice(0, startIndexInPath + 1),
    {
      referenceId: reference.referenceId,
      componentId: lastComponentId,
      direction:
        start === 'start'
          ? ReferenceDirection.OUTGOING
          : ReferenceDirection.INCOMING,
      isParentChildReference: false,
    },
    ...componentPath.slice(endIndexInPath + 1),
  ];

  // We can only register the component and reference to keep after all
  // collapsing rules of a path have been applied, because we don't want to
  // keep components which are part of a path which is later collapsed.
  // Only the components and references of the final collapsed path should be
  // kept.

  return collapsedPath;
};

const toRegionKey = (
  referenceTypeId: string,
  pathIndex: number,
  { startIndex, length, start }: PathMatch
) => `${referenceTypeId}:${pathIndex}:${startIndex}:${length}:${start}`;

const getAssignedRegions = (
  paths: Path[],
  pathCollapsingRules: PathCollapsingRule[]
) =>
  new Set(
    assignRegionsToRules(
      pathCollapsingRules.flatMap(rule =>
        // Overlap validation off, as at the other call sites: a crossing
        // overlap is reported by the caller's own check, and swallowing the
        // matches here would silently leave that path uncollapsed instead.
        getMatchingPaths(paths, rule, true, false)
      )
    ).map(({ rule, match, pathIndex }) =>
      toRegionKey(rule.referenceTypeId, pathIndex, match)
    )
  );

const getAllMatchesForPath = (
  path: Path,
  pathCollapsingRules: PathCollapsingRule[],
  pathIndex: number,
  assignedRegions: Set<string>
) => {
  const allPathMatches = pathCollapsingRules.flatMap(rule => {
    const { path: rulePath } = rule;
    const invertedRulePath = invertTriplePath(rulePath);
    const isSymmetric = invertedRulePath.every((triple, index) =>
      isSameTriple(triple, rulePath[index])
    );
    const matches = isSymmetric
      ? getMatchesForPath(path, rulePath, 'start')
      : [
          ...getMatchesForPath(path, rulePath, 'start'),
          ...getMatchesForPath(path, invertedRulePath, 'end'),
        ];

    return matches.map(match => ({
      match,
      rule,
      matchKey: getMatchKey(match, rule.referenceTypeId),
    }));
  });

  // Resolve nested same-end overlaps (keep the longest match), then sort by end
  // index descending so collapsing from the end of the path does not interfere
  // with the indexes of earlier matches.
  const resolvedMatches = keepLongestMatchPerEndPosition(
    uniqBy(allPathMatches, 'matchKey').filter(({ rule, match }) =>
      assignedRegions.has(toRegionKey(rule.referenceTypeId, pathIndex, match))
    )
  ).toSorted(sortByEndIndexDescending);

  if (
    !validateMatchesAgainstOverlaps(resolvedMatches.map(({ match }) => match))
  ) {
    throw Error('Overlapping matches found for path in getAllMatchesForPath');
  }

  return resolvedMatches;
};

type RemoveAllBranchesOfCollapsedPathsArgs = {
  collapsedInstancePaths: ComponentPath[][];
  accumulatedResult: AccumulatedResult;
};

const removeAllBranchesOfCollapsedPaths = ({
  collapsedInstancePaths,
  accumulatedResult,
}: RemoveAllBranchesOfCollapsedPathsArgs) =>
  collapsedInstancePaths.map((collapsedPaths, index) => {
    if (accumulatedResult.collapsedPathIndexes.has(index)) {
      return collapsedPaths;
    }

    return collapsedPaths.map(componentPath => {
      const branchOffIndex = componentPath.findIndex(({ referenceId }) =>
        accumulatedResult.removedReferenceIds.has(referenceId)
      );
      if (branchOffIndex === -1) {
        registerComponentsAndReferencesToKeep(accumulatedResult, componentPath);
        return componentPath;
      }
      const collapsedPath = componentPath.slice(0, branchOffIndex);
      const cutOffSubPath = componentPath.slice(branchOffIndex);
      registerComponentsAndReferencesToKeep(accumulatedResult, collapsedPath);
      markComponentAndReferencesToRemove(accumulatedResult, cutOffSubPath);
      return collapsedPath;
    });
  });

const sortByEndIndexDescending = <T extends { match: PathMatch }>(
  { match: { startIndex: startIndexA, length: lengthA } }: T,
  { match: { startIndex: startIndexB, length: lengthB } }: T
) => {
  return startIndexB + lengthB - (startIndexA + lengthA);
};

const getPathsCollapsingRulesAndPathResults = (
  loadedState: TraversalLoadedState
) => {
  const {
    data: { paths, pathCollapsingRules = [] },
    componentIdsAndReferences: { pathResults } = {
      pathResults: [],
    },
  } = loadedState;

  return {
    pathResults,
    paths,
    startSetExpansion: loadedState.data.startSetExpansion,
    pathCollapsingRules: pathCollapsingRules.filter(({ isActive }) => isActive),
  };
};

const getRootWorkspace = (
  componentsById: Record<ArdoqId, APIComponentAttributes>,
  id: ArdoqId
) => componentsById[id]?.rootWorkspace ?? '';

const getReferenceId = (sourceId: string, targetId: string) =>
  `${sourceId}-${targetId}`;

// The rule id is part of the key: two rules can hold the same path, and each
// needs its own match to survive so the assignment can hand them a region each.
const getMatchKey = (
  { rulePathKey, startIndex, start }: PathMatch,
  referenceTypeId: string
) => `${referenceTypeId}-${rulePathKey}-${startIndex}-${start}`;

const registerComponentsAndReferencesToKeep = (
  accumulatedResult: AccumulatedResult,
  componentSubPath: ComponentPath
) => {
  componentSubPath.forEach(({ componentId, referenceId }) => {
    accumulatedResult.componentIdsToKeep.add(componentId);
    accumulatedResult.referencesToKeep.add(referenceId);
  });
};

const markComponentAndReferencesToRemove = (
  accumulatedResult: AccumulatedResult,
  componentSubPath: ComponentPath
) =>
  componentSubPath.forEach(({ componentId, referenceId }) => {
    accumulatedResult.removedReferenceIdCandidates.add(referenceId);
    accumulatedResult.removedComponentIdCandidates.add(componentId);
  });

type AccumulatedResult = {
  newReferences: VirtualReference[];
  removedReferenceIds: Set<string>;
  removedReferenceIdCandidates: Set<string>;
  removedComponentIdCandidates: Set<string>;
  componentIdsToKeep: Set<string>;
  referencesToKeep: Set<string>;
  collapsedPathIndexes: Set<number>;
};
